<script lang="ts" setup>
import type { CategoryItem, Product, RawProduct } from '~/types/searchTypes'
import { SortDirection } from '~/types/searchTypes'

import ProductCarousel from '../ProductCarousel.vue'
import { useCategoryStore } from '~/store/categoryStore'
import { useLanguageStore } from '~/store/language'
import computeAllCategoryChildrenIdsByParentIds from '~/utils/config/search/computeAllCategoryChildrenIdsByParentIds'
import randomizeArray from '~/utils/randomizeArray'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import parseProductSlug from '~/utils/parseProductSlug'
import type { CarouselProduct } from '~/types/Product'
import getUniqueProducts from '~/utils/search/getUniqueProducts'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const products = ref<Array<Product | CarouselProduct>>([])
const categoryStore = useCategoryStore()
const locale = useState<string>('locale.setting')

const props = defineProps<{
  manufacturer?: string[]
  limit: number
  sort: keyof typeof SortDirection
  backgroundColor?: string
  categories?: number[]
  randomizeProducts?: boolean
  discount?: number
  lockedProducts?: {
    productSlug: TranslatableString
  }[]
}>()

watch(
  (): [CategoryItem[]] => [categoryStore.getCategories],
  async ([categoryItems]) => {
    try {
      let categories: undefined | number[]

      if (props.categories) {
        categories = [...props.categories, ...computeAllCategoryChildrenIdsByParentIds(props.categories, categoryItems)]
      }

      const response = await $fetch<RawProduct[]>('/api/hooks/widgetProducts', {
        method: 'POST',
        body: {
          locale: locale.value,
          limit: props.randomizeProducts ? 30 : props.limit,
          manufacturer: props.manufacturer,
          categories,
          sort: Array.isArray(props.sort) ? props.sort : [props.sort],
          languageId: useLanguageStore().getLanguageId,
          discount: props.discount,
        },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      const fResponse = getUniqueProducts(response)

      if (props.lockedProducts?.length) {
        const productsRes = await $fetch('/api/market/products', {
          method: 'POST',
          body: {
            products: props.lockedProducts.map((lockedProduct) => parseProductSlug(lockedProduct.productSlug)),
            langId: useLanguageStore().getLanguageId,
          },
          ...fetchRetryParameters,
          timeout: 3000,
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })

        const lockedProducts = productsRes.filter(
          (product) => product?.id && !product?.is_sold_out
        ) as CarouselProduct[]

        const unlockedProducts = props.randomizeProducts
          ? randomizeArray(fResponse, props.limit - lockedProducts.length)
          : fResponse.slice(0, props.limit - lockedProducts.length)

        products.value = [...lockedProducts, ...unlockedProducts]
      } else {
        products.value = props.randomizeProducts ? randomizeArray(fResponse, props.limit) : fResponse
      }
    } catch (error) {}
  },
  {
    immediate: true,
  }
)
</script>

<template>
  <section
    :style="{
      backgroundColor: props.backgroundColor,
    }">
    <ProductCarousel :products="products" />
  </section>
</template>
